// The pure logic of the renderer: values of a form (flat for a plain group, a list of objects for a repeatable one,
// spec §10), the repeat rules, error paths and how errors from FormHelper and from the server become one map.
// No React, no aliases: runs under `node --test`.
import { LIMITS, get_field_state } from "./form_helper.js";
import { REPEAT_MIN, REPEAT_MAX } from "./constants.js";

const is_object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

export const groups_of = (version) => (Array.isArray(version?.groups) ? version.groups : []);
export const fields_of = (group) => (Array.isArray(group?.fields) ? group.fields : []);

// what the admin gave a field to start with (spec §5, §8): a new instance of a repeatable group gets the same
const defaults_of = (group) => {
    const values = {};
    fields_of(group).forEach((field) => {
        if (typeof field?.key === "string" && field.default_value !== undefined && field.default_value !== null && field.default_value !== "") {
            values[field.key] = field.default_value;
        }
    });
    return values;
};

// every field starts with its default (when the admin set one); a repeatable group starts with one instance
export const initial_values = (version) => {
    const values = {};
    groups_of(version).forEach((group) => {
        if (group?.is_repeatable) {
            values[group.key] = [defaults_of(group)];
        } else {
            Object.assign(values, defaults_of(group));
        }
    });
    return values;
};

// the values a new instance of this group starts with (the fields' defaults), for a site that builds the instances itself
export const group_defaults = (group) => defaults_of(group);

export const instance_count = (values, group) => (group?.is_repeatable ? Math.max(REPEAT_MIN, Array.isArray(values?.[group.key]) ? values[group.key].length : REPEAT_MIN) : 1);

export const read_field = (values, group, index, field_key) => {
    if (group?.is_repeatable) {
        return values?.[group.key]?.[index]?.[field_key];
    }
    return values?.[field_key];
};

const is_unset = (value) => value === undefined || value === null;

// a new values object with one field changed; `undefined` removes the key. Never mutates the old one.
export const write_field = (values, group, index, field_key, value) => {
    if (group?.is_repeatable) {
        const list = Array.isArray(values?.[group.key]) ? values[group.key].slice() : [{}];
        while (list.length <= index) {
            list.push({});
        }
        const instance = { ...(list[index] || {}) };
        if (is_unset(value)) {
            delete instance[field_key];
        } else {
            instance[field_key] = value;
        }
        list[index] = instance;
        return { ...values, [group.key]: list };
    }
    const next = { ...values };
    if (is_unset(value)) {
        delete next[field_key];
    } else {
        next[field_key] = value;
    }
    return next;
};

// "افزودن": a new instance below the last one, with the fields' defaults, up to the maximum
export const add_instance = (values, group) => {
    if (!group?.is_repeatable) {
        return values;
    }
    const list = Array.isArray(values?.[group.key]) ? values[group.key] : [{}];
    if (list.length >= REPEAT_MAX) {
        return values;
    }
    return { ...values, [group.key]: [...list, defaults_of(group)] };
};

// "حذف": one instance goes, the first one always stays when it is the only one
export const remove_instance = (values, group, index) => {
    if (!group?.is_repeatable) {
        return values;
    }
    const list = Array.isArray(values?.[group.key]) ? values[group.key] : [{}];
    if (list.length <= REPEAT_MIN || index < 0 || index >= list.length) {
        return values;
    }
    return { ...values, [group.key]: list.filter((item, position) => position !== index) };
};

// the path the server and FormHelper use for a field's errors: `f_x`, or `g_x.<index>.f_y` inside a repeatable group
export const error_path = (group, index, field_key) => (group?.is_repeatable ? `${group.key}.${index}.${field_key}` : field_key);

// removes the errors of a path (and of the paths under it) once the visitor changes that field
export const without_errors = (errors, path) => {
    if (!errors || !Object.prototype.hasOwnProperty.call(errors, path)) {
        return errors;
    }
    const next = { ...errors };
    delete next[path];
    return next;
};

// the instances shift after a removal, so the errors of that group are dropped rather than pointing at the wrong one
export const without_group_errors = (errors, group_key) => {
    const next = {};
    Object.keys(errors || {}).forEach((path) => {
        if (!path.startsWith(`${group_key}.`)) {
            next[path] = errors[path];
        }
    });
    return next;
};

// ---- errors -------------------------------------------------------------------------------------------------------

const detail_of = (error) => {
    if (error.max !== undefined) {
        return error.max;
    }
    if (error.min !== undefined) {
        return error.min;
    }
    if (error.mode !== undefined) {
        return error.mode;
    }
    if (Array.isArray(error.allowed)) {
        return error.allowed.join(",");
    }
    return undefined;
};

// FormHelper.validate_submission errors -> { path: ["code" | "code||detail"] }, the same map the server answers with
export const errors_to_map = (errors) => {
    const map = {};
    (errors || []).forEach((error) => {
        const path = error.path || "_form";
        const detail = detail_of(error);
        if (!map[path]) {
            map[path] = [];
        }
        map[path].push(detail === undefined ? error.code : `${error.code}||${detail}`);
    });
    return map;
};

// the first error in the order the fields are on the page, so the page can scroll to and focus it
export const first_error_path = (version, errors) => {
    const paths = Object.keys(errors || {}).filter((path) => path !== "_form");
    if (paths.length === 0) {
        return null;
    }
    for (const group of groups_of(version)) {
        const count = group?.is_repeatable ? REPEAT_MAX : 1;
        for (let index = 0; index < count; index += 1) {
            for (const field of fields_of(group)) {
                const path = error_path(group, index, field.key);
                if (paths.includes(path)) {
                    return path;
                }
            }
        }
    }
    return paths[0];
};

/*
 * What a failed createFormSubmission means (the contract of the server, Phase 5):
 *   login_required (code 401)  captcha_error (999)  too_many_submissions (403, errors: { retry_after_seconds })
 *   validation_error with errors keyed by path     not_found (404)
 * `graphql_errors` is core's `result.errors` (the graphQLErrors array, false/undefined on a network failure).
 * Returns { kind, errors, retry_after_seconds } with kind in login_required | captcha | throttled | invalid | not_found
 * | network | unknown, and `errors` a { path: [codes] } map (the form-level text under `_form`).
 */
export const interpret_submit_failure = (graphql_errors) => {
    const first = Array.isArray(graphql_errors) ? graphql_errors[0] : undefined;
    if (!first) {
        return { kind: "network", errors: { _form: ["network"] } };
    }
    const message = String(first.message ?? "");
    const detail = is_object(first.errors) ? first.errors : (is_object(first.extensions?.errors) ? first.extensions.errors : null);
    const code = String(first.code ?? first.extensions?.code ?? "");
    if (message === "login_required" || code === "401") {
        return { kind: "login_required", errors: { _form: ["login_required"] } };
    }
    if (message === "captcha_error") {
        return { kind: "captcha", errors: { _form: ["captcha_error"] } };
    }
    if (message === "too_many_submissions") {
        const seconds = Number(detail?.retry_after_seconds);
        return { kind: "throttled", errors: { _form: [Number.isFinite(seconds) ? `too_many_submissions||${seconds}` : "too_many_submissions"] }, retry_after_seconds: Number.isFinite(seconds) ? seconds : undefined };
    }
    if (code === "404") {
        return { kind: "not_found", errors: { _form: ["not_found"] } };
    }
    if (detail) {
        const errors = {};
        Object.keys(detail).forEach((path) => {
            const list = Array.isArray(detail[path]) ? detail[path] : [detail[path]];
            // the form itself is "_form"; a version/meta problem has no field of its own
            const target = path === "version" ? "_form" : (path === "meta.extra" ? "_form" : path);
            errors[target] = [...(errors[target] || []), ...list.map(String).map((item) => (path === "version" ? "version_conflict" : item))];
        });
        return { kind: "invalid", errors };
    }
    return { kind: "unknown", errors: { _form: ["unknown"] } };
};

// ---- the request --------------------------------------------------------------------------------------------------

/*
 * The input of createFormSubmission (spec §8, "مسیر ارسال فرم"):
 *   { form_definition_id | form_key, version, values, meta: { started_at, duration_ms, page_url, extra }, captcha }
 * `values` are FormHelper's clean values (hidden and disabled fields already left out, options as { title, value }).
 */
export const build_submit_input = ({ definition, clean_values, started_at_ms, now_ms, page_url, extra, captcha, submission_id }) => {
    const meta = {};
    if (Number.isFinite(started_at_ms)) {
        meta.started_at = new Date(started_at_ms).toISOString();
        const duration = Math.max(0, Math.min(now_ms - started_at_ms, LIMITS.DURATION_MAX_MS));
        meta.duration_ms = Number.isFinite(duration) ? Math.round(duration) : undefined;
    }
    if (typeof page_url === "string" && page_url !== "") {
        meta.page_url = page_url;
    }
    if (is_object(extra) && Object.keys(extra).length > 0) {
        meta.extra = extra;
    }
    const input = {
        version: definition?.version?.version ?? definition?.current_version,
        values: clean_values,
        meta,
    };
    if (definition?.id) {
        input.form_definition_id = definition.id;
    } else if (definition?.key) {
        input.form_key = definition.key;
    }
    if (captcha) {
        input.captcha = captcha;
    }
    // the answer this send changes (the visitor's own draft or sent answer): the server edits that row and makes no new one
    if (typeof submission_id === "string" && submission_id !== "") {
        input.submission_id = submission_id;
    }
    return input;
};

// the renderer's view of one field: its state (visible / disabled / required) from FormHelper
export const state_of_field = (form_state, group, index, field_key) => {
    if (group?.is_repeatable) {
        return get_field_state(form_state, field_key, index);
    }
    return get_field_state(form_state, field_key, 0);
};

const is_blank = (value) => value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

/*
 * The rules a site gives its own fields (`custom_rules`: { [custom_key]: (value, field, values) => code | null | undefined }).
 * FormHelper cannot know what a custom value must look like (a bank card's check digit), so the site says it: a code is an
 * error of that field, shown with the text the site gives that code in `messages.errors`. Only a shown, filled field is judged
 * (empty is the "required" rule's business), and a field that already has an error keeps it.
 * Returns the errors in the shape of validate_submission's: { path, code, key, group_key? }.
 */
export const custom_rule_errors = (version, values, form_state, rules, already = []) => {
    if (!rules || typeof rules !== "object") {
        return [];
    }
    const taken = new Set((already || []).map((error) => error.path));
    const found = [];
    groups_of(version).forEach((group) => {
        if (form_state?.groups && !form_state.groups[group?.key]?.visible) {
            return;
        }
        const count = instance_count(values, group);
        for (let index = 0; index < (group?.is_repeatable ? count : 1); index += 1) {
            fields_of(group).forEach((field) => {
                const rule = typeof field?.type === "string" && field.type.startsWith("custom:") ? rules[field.type.slice("custom:".length)] : undefined;
                if (typeof rule !== "function") {
                    return;
                }
                const path = error_path(group, index, field.key);
                const value = read_field(values, group, index, field.key);
                if (taken.has(path) || is_blank(value) || !state_of_field(form_state, group, index, field.key)?.visible) {
                    return;
                }
                const code = rule(value, field, values);
                if (code) {
                    found.push(group?.is_repeatable ? { path, code, key: field.key, group_key: group.key } : { path, code, key: field.key });
                }
            });
        }
    });
    return found;
};

/*
 * A file the visitor already sent comes back from the server as the stored value { media_id, url, name, size } and goes back unchanged: it is not
 * a file to read and check again (FormHelper's input mode would refuse it). stored_files_of(version, values) -> { values, kept }
 *   values   the values without those files, to be checked
 *   kept     { field_key: stored value } - to be put back in the clean values, and a "required" for them is not a fault
 * Plain groups only; a file in a repeatable group is chosen again.
 */
export const is_stored_file = (value) => is_object(value) && typeof value.media_id === "string" && value.media_id !== "" && typeof value.content !== "string";

export const stored_files_of = (version, values) => {
    const rest = { ...values };
    const kept = {};
    groups_of(version).forEach((group) => {
        if (group?.is_repeatable) {
            return;
        }
        fields_of(group).forEach((field) => {
            if (field.type === "file" && is_stored_file(rest[field.key])) {
                kept[field.key] = rest[field.key];
                delete rest[field.key];
            }
        });
    });
    return { values: rest, kept };
};
/*
 * Files sent through the web-server before the answer (useFormRenderer's `upload_files`, added for jument): the chosen file
 * is still read in the browser (for its preview and the same checks as before), but at submit it goes to the server as a
 * file (multipart), not as base64 inside the answer; the answer then carries { upload_id, upload_token }.
 */

// a file the visitor chose and the server has not seen: { content: "data:...;base64,...", file_name, is_base_64: true }
const is_input_file = (value) => is_object(value) && value.is_base_64 === true && typeof value.content === "string" && value.content !== "";

// every chosen file in the clean values -> [{ path, value }] (path "f_x", or "g_x.<index>.f_x" in a repeatable group)
export const input_files_of = (version, values) => {
    const found = [];
    groups_of(version).forEach((group) => {
        const fields = fields_of(group).filter((field) => field?.type === "file");
        if (fields.length === 0) {
            return;
        }
        if (group.is_repeatable) {
            (Array.isArray(values?.[group.key]) ? values[group.key] : []).forEach((instance, index) => {
                fields.forEach((field) => {
                    if (is_input_file(instance?.[field.key])) {
                        found.push({ path: error_path(group, index, field.key), value: instance[field.key] });
                    }
                });
            });
            return;
        }
        fields.forEach((field) => {
            if (is_input_file(values?.[field.key])) {
                found.push({ path: field.key, value: values[field.key] });
            }
        });
    });
    return found;
};

// "data:image/png;base64,AAAA" -> Blob (the bytes the visitor chose, back as a file to send)
export const data_url_to_blob = (content) => {
    const [head, data = ""] = String(content).split(",");
    const type = /^data:([^;,]+)/.exec(head)?.[1] || "application/octet-stream";
    const binary = typeof atob === "function" ? atob(data) : Buffer.from(data, "base64").toString("binary");
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type });
};

// the clean values with each sent file replaced by its upload -> { values, missing: [paths the server did not answer for] }
export const with_uploaded_files = (values, paths, uploaded) => {
    const by_path = new Map((Array.isArray(uploaded) ? uploaded : []).map((item) => [item?.path, item]));
    const next = { ...values };
    const missing = [];
    paths.forEach((path) => {
        const item = by_path.get(path);
        if (!item?.upload_id || !item?.upload_token) {
            missing.push(path);
            return;
        }
        const ref = { upload_id: item.upload_id, upload_token: item.upload_token };
        const parts = path.split(".");
        if (parts.length === 3) {
            const [group_key, index, field_key] = parts;
            const list = Array.isArray(next[group_key]) ? next[group_key].slice() : [];
            list[Number(index)] = { ...(list[Number(index)] || {}), [field_key]: ref };
            next[group_key] = list;
            return;
        }
        next[path] = ref;
    });
    return { values: next, missing };
};
